/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import Joi from 'joi'
import {
  allowedFileTypes,
  allowedAttestationType,
  allowedOwnership,
  allowedIpType
} from '@/configs/default-values'

const decimalNumber = Joi.number()
  .positive()
  .messages({
    'number.base': 'This field must be a number',
    'number.positive': 'Value must be positive'
  })

const integerNumber = Joi.number()
  .integer()
  .positive()
  .messages({
    'number.base': 'This field must be a number',
    'number.integer': 'This field must be an integer',
    'number.positive': 'Value must be positive'
  })

const stringValue = Joi.string().messages({
  'string.base': 'This field must be a string'
})

const stringOrArraySchema = Joi.alternatives()
  .try(
    Joi.string(),
    Joi.array().items(Joi.string())
  )
  .custom((value) => {
    return Array.isArray(value) ? value : value.split(',').map((item) => item.trim())
  })

const errorFileType = `Must be a valid File type: ${allowedFileTypes.join(', ')}`
const errorIpType = `Must be a valid IP Type: ${allowedIpType.join(', ')}`
const errorAttestationType = `Must be a valid Attestation Type: ${allowedAttestationType.join(', ')}`
const errorOwnership = `Must be a valid Ownership: ${allowedOwnership.join(', ')}`

export const schemaValidation = Joi.object({
  filename: stringValue.required(),
  fileType: Joi.string()
    .valid(...allowedFileTypes)
    .required()
    .messages({
      'any.only': errorFileType,
      'any.required': errorFileType,
      'any.empty': errorFileType
    }),
  title: stringValue.required(),
  ipType: Joi.string()
    .valid(...allowedIpType)
    .required()
    .messages({
      'any.only': errorIpType,
      'any.required': errorIpType,
      'any.empty': errorIpType
    }),
  attestationType: Joi.string()
    .valid(...allowedAttestationType)
    .required()
    .messages({
      'any.only': errorAttestationType,
      'any.required': errorAttestationType,
      'any.empty': errorAttestationType
    }),
  ownership: Joi.string()
    .valid(...allowedOwnership)
    .required()
    .messages({
      'any.only': errorOwnership,
      'any.required': errorOwnership,
      'any.empty': errorOwnership
    }),
  creators: stringOrArraySchema.required(),
  publishers: stringOrArraySchema.required(),
  creatorUsageRoyalty: decimalNumber.required(),
  publisherUsageRoyalty: integerNumber.required()
})
